import React,{useEffect,useMemo,useState} from 'react';
import SafeIcon from '../../common/SafeIcon';
import {fetchSellerTaxReport} from '../../services/sellerTaxReportService';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiCalendar,
  FiDownload,
  FiLoader,
  FiRefreshCw,
  FiShield
}=FiIcons;

const money=(value)=>{
  if (value===null || value===undefined) return 'Pending calculation';

  return `$${Number(value).toLocaleString(undefined,{
    minimumFractionDigits:2,
    maximumFractionDigits:2
  })}`;
};

const SellerTaxTab=({seller})=>{
  const today=new Date();
  const defaultEnd=today.toISOString().slice(0,10);
  const defaultStart=new Date(
    today.getTime()-29*24*60*60*1000
  ).toISOString().slice(0,10);
  const [startDate,setStartDate]=useState(defaultStart);
  const [endDate,setEndDate]=useState(defaultEnd);
  const [rows,setRows]=useState([]);
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState('');

  const loadReport=async()=>{
    if (!seller?.id) return;

    setLoading(true);
    setError('');

    try {
      setRows(await fetchSellerTaxReport(
        seller.id,
        startDate,
        endDate
      ));
    } catch (loadError) {
      setError(loadError.message || 'Unable to load tax reporting.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadReport();
  },[seller?.id]);

  const totals=useMemo(()=>rows.reduce((result,row)=>{
    if (row.tax_amount!==null && row.tax_amount!==undefined) {
      result.tax+=Number(row.tax_amount);
      result.finalizedCount+=1;
    }

    if (
      row.seller_tax_amount!==null
      && row.seller_tax_amount!==undefined
    ) {
      result.sellerTax+=Number(row.seller_tax_amount);
    }

    if (row.refund_amount!==null && row.refund_amount!==undefined) {
      result.refunds+=Number(row.refund_amount);
    }

    return result;
  },{
    tax:0,
    sellerTax:0,
    refunds:0,
    finalizedCount:0
  }),[rows]);

  const exportReport=()=>{
    const header=[
      'Order ID',
      'Date',
      'Buyer',
      'Product Subtotal',
      'Stripe Tax',
      'Seller Tax',
      'Total Paid',
      'Refund Amount',
      'Tax Liability',
      'Tax Status'
    ];

    const body=rows.map((row)=>[
      row.order_reference,
      row.order_date,
      row.buyer_name,
      row.product_subtotal ?? 'Pending calculation',
      row.tax_amount ?? 'Pending calculation',
      row.seller_tax_amount ?? 'Pending calculation',
      row.total_paid ?? 'Pending calculation',
      row.refund_amount ?? 'Pending calculation',
      row.tax_liable_party || 'Pending calculation',
      row.tax_status
    ]);

    const csv=[header,...body]
      .map((line)=>line.map((value)=>`"${String(value).replaceAll('"','""')}"`).join(','))
      .join('\n');
    const blob=new Blob([csv],{type:'text/csv;charset=utf-8'});
    const url=URL.createObjectURL(blob);
    const link=document.createElement('a');

    link.href=url;
    link.download=`sohlocal-tax-report-${startDate}-to-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-4xl font-black tracking-tighter text-slate-900">
          Tax <span className="text-indigo-600">Reporting</span>
        </h2>
        <p className="mt-2 text-sm font-medium text-slate-500">
          Stripe Tax amounts for orders placed while your GST/HST registration was valid.
        </p>
      </div>

      <div className="flex items-start rounded-2xl border border-emerald-100 bg-emerald-50 px-5 py-4 text-sm font-bold text-emerald-800">
        <SafeIcon icon={FiShield} className="mr-3 mt-0.5 text-lg" />
        <span>
          Registration on file: {seller.gst_hst_registration_number}
        </span>
      </div>

      {error && (
        <div className="flex items-center rounded-2xl border border-red-100 bg-red-50 px-5 py-4 text-sm font-bold text-red-700">
          <SafeIcon icon={FiAlertCircle} className="mr-3 text-lg" />
          {error}
        </div>
      )}

      <section className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="text-xs font-black uppercase tracking-widest text-slate-400">
              From
              <span className="relative mt-2 block">
                <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="date"
                  value={startDate}
                  max={endDate}
                  onChange={(event)=>setStartDate(event.target.value)}
                  className="rounded-2xl bg-slate-50 py-4 pl-11 pr-4 font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </span>
            </label>
            <label className="text-xs font-black uppercase tracking-widest text-slate-400">
              To
              <span className="relative mt-2 block">
                <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(event)=>setEndDate(event.target.value)}
                  className="rounded-2xl bg-slate-50 py-4 pl-11 pr-4 font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </span>
            </label>
          </div>

          <div className="flex gap-3">
            <button
              onClick={loadReport}
              disabled={loading}
              className="flex items-center rounded-2xl bg-indigo-600 px-6 py-4 text-xs font-black uppercase tracking-widest text-white disabled:opacity-50"
            >
              <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={exportReport}
              disabled={!rows.length}
              className="flex items-center rounded-2xl border border-slate-200 px-6 py-4 text-xs font-black uppercase tracking-widest text-slate-600 disabled:opacity-50"
            >
              <SafeIcon icon={FiDownload} className="mr-2" />
              Export
            </button>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {[
          ['Stripe tax collected',money(totals.tax)],
          ['Tax attributable to seller',money(totals.sellerTax)],
          ['Refunded tax-inclusive amounts',money(totals.refunds)]
        ].map(([label,value])=>(
          <div key={label} className="rounded-[2rem] border border-slate-100 bg-white p-6 shadow-sm">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
            <p className="mt-2 text-2xl font-black text-slate-900">{value}</p>
          </div>
        ))}
      </div>

      <section className="overflow-hidden rounded-[2.5rem] border border-slate-100 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-8">
          <h3 className="text-2xl font-black text-slate-900">Tax detail</h3>
          <p className="mt-1 text-sm font-medium text-slate-500">
            {totals.finalizedCount} of {rows.length} orders have finalized Stripe tax values.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1250px]">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] font-black uppercase tracking-widest text-slate-400">
                {[
                  'Order ID','Date','Buyer','Product subtotal','Stripe Tax',
                  'Seller tax','Total paid','Refund amount','Tax liability','Status'
                ].map((heading)=>(
                  <th key={heading} className="px-5 py-4">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {rows.map((row)=>(
                <tr key={row.order_id} className="text-sm">
                  <td className="px-5 py-5 font-black text-indigo-600">{row.order_reference}</td>
                  <td className="px-5 py-5 text-slate-500">{row.order_date}</td>
                  <td className="px-5 py-5 font-bold text-slate-900">{row.buyer_name}</td>
                  <td className="px-5 py-5">{money(row.product_subtotal)}</td>
                  <td className="px-5 py-5 font-black">{money(row.tax_amount)}</td>
                  <td className="px-5 py-5 text-emerald-600">{money(row.seller_tax_amount)}</td>
                  <td className="px-5 py-5 font-black">{money(row.total_paid)}</td>
                  <td className="px-5 py-5 text-amber-600">{money(row.refund_amount)}</td>
                  <td className="px-5 py-5 text-xs font-black uppercase">{row.tax_liable_party || 'Pending calculation'}</td>
                  <td className="px-5 py-5 text-xs font-black uppercase">{row.tax_status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {!rows.length && !loading && (
            <div className="p-16 text-center text-sm font-bold text-slate-400">
              No registered-seller tax transactions were found for this date range.
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SellerTaxTab;